import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { catchError, timeout } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  constructor(private http: HttpClient, private toastr: ToastrService) {}

  /**
   * Sends a GET request to the specified URL with optional query parameters.
   *
   * @param url - The URL to send the GET request to.
   * @param params - An array of key-value pairs representing the query parameters.
   * @returns An Observable that emits the response data of type T.
   */
  public get<T>(
    url: string,
    params: { key: string; value: any }[] = [],
    customErrorHandling: boolean = false
  ): Observable<T> {
    //todo: remove the token from here
    let token = this.getToken();
    let httpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
    });
    if (token) {
      httpHeaders = httpHeaders.append('Authorization', 'Bearer ' + token);
    }
    let options = {
      headers: httpHeaders,
      params: this.generateQueryParams(params),
    };

    return this.http.get<T>(url, options).pipe(
      catchError((err) => {
        if (!customErrorHandling) {
          let unauthorized = this.unauthorizedErrorHandling(err);
          if (!unauthorized) {
            // show toastr error
            let errMessage = this.errorMessageHandling(err);
            this.toastr.error(
              errMessage?.message != ''
                ? errMessage.message
                : 'An unknown error has occured!',
              errMessage?.header
            );
          }
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * Generates an instance of HttpParams with the provided key-value pairs.
   *
   * @param params - An array of objects containing the key-value pairs.
   * @returns An instance of HttpParams with the provided key-value pairs.
   */
  private generateQueryParams(
    params: { key: string; value: any }[] = []
  ): HttpParams {
    let httpParams = new HttpParams();
    params.forEach((param) => {
      httpParams = httpParams.append(param.key, param.value);
    });
    return httpParams;
  }

  public post<T>(
    url: string,
    data?: any,
    time: number = 100000
  ): Observable<T> {
    return new Observable((observer) => {
      let httpHeaders = new HttpHeaders();
      let token = this.getToken();
      httpHeaders = new HttpHeaders({
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        Authorization: 'Bearer ' + token,
      });
      let options = {
        headers: httpHeaders,
      };
      this.modifyDateJson();
      this.http
        .post<T>(url, data, options)
        .pipe(timeout(time))
        .subscribe({
          next: (result) => {
            observer.next(result);
            observer.complete();
          },
          error: (err: HttpErrorResponse) => {
            if (err.status == 200) {
              //when Response is not application/json ex: some API return text/csv
              observer.next(err.error.text);
              observer.complete();
            } else {
              let unauthorized = this.unauthorizedErrorHandling(err);
              if (!unauthorized) {
                let errMessage = this.errorMessageHandling(err);
                this.toastr.error(
                  errMessage.message != ''
                    ? errMessage.message
                    : 'An unknown error has occured!',
                  errMessage.header
                );
              }
              observer.error(err);
            }
          },
        });
    });
  }

  public postFile(
    url: string,
    data?: any,
    time: number = 100000
  ): Observable<{ title: string; file: Blob }> {
    return new Observable((observer) => {
      //todo: remove the token from here
      let token = this.getToken();
      let httpHeaders = new HttpHeaders({
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
      });
      if (token) {
        httpHeaders = httpHeaders.append('Authorization', 'Bearer ' + token);
      }
      this.modifyDateJson();
      this.http
        .post(url, data, {
          headers: httpHeaders,
          observe: 'response',
          responseType: 'blob',
        })
        .pipe(timeout(time))
        .subscribe({
          next: (result) => {
            observer.next({
              title: result.headers.get('Content-Disposition') ?? '',
              file: result.body ?? new Blob(),
            });
            observer.complete();
          },
          error: (err) => {
            if (
              'application/json; charset=utf-8' ===
              err.headers.get('Content-Type')
            ) {
              const reader = new FileReader();
              reader.addEventListener('loadend', (e) => {
                const readerTarget = e.target as FileReader | null;
                const er =
                  readerTarget && readerTarget.result
                    ? JSON.parse(readerTarget.result as string)?.Message
                    : '';
                let unauthorized = this.unauthorizedErrorHandling(err);
                if (!unauthorized) {
                  let errMessage = this.errorMessageHandling(err);
                  this.toastr.error(
                    er ?? 'An unknown error has occured!',
                    errMessage.header,
                    { enableHtml: true }
                  );
                }
              });
              reader.readAsText(err.error);
            }
            observer.error(err);
          },
        });
    });
  }

  private unauthorizedErrorHandling(err: HttpErrorResponse) {
    let retVal = false;
    if (err.status == 401) {
      //Unauthorized Error Handling
      let token = localStorage.getItem('Token');
      if (token) {
        window.location.href = '/A/Unauthorized/Main/Unauthorized';
      } else {
        //redirect to Login page if there is no JWT TOKEN available
        window.location.href = '/';
      }
      retVal = true;
    }
    return retVal;
  }

  private errorMessageHandling(err: HttpErrorResponse): {
    message: string;
    header: string;
  } {
    let header = 'API Error';
    let message = 'Exception Message';

    if (err.error) {
      //Handle 4XX error and 5XX Error
      header = 'API Error - ' + err.status;
      if (err.status == 451) {
        header = 'Error';
      }

      if (err.error.Message) {
        message = err.error.Message;
      }
    } else {
      if (err.message) {
        //Handle timeout scenario we do not receive HTTP StatusCode
        header = 'API Error - ' + err.name;
        message = err.message;
      }
    }
    return { header: header, message: message };
  }

  private getToken() {
    let user = localStorage.getItem('user');
    let token = null;
    if (user) {
      token = JSON.parse(user).access_token;
    }
    return token;
  }

  private modifyDateJson() {
    Date.prototype.toJSON = function () {
      let v =
        this.getFullYear() +
        '-' +
        pad(this.getMonth() + 1) +
        '-' +
        pad(this.getDate()) +
        'T' +
        pad(this.getHours()) +
        ':' +
        pad(this.getMinutes()) +
        ':' +
        pad(this.getSeconds()) +
        '.' +
        '000';
      return v;
    };

    function pad(number: number) {
      if (number < 10) {
        return '0' + number;
      }
      return number;
    }
  }
}
